import {
  IVideoDataEditableThumbnails,
  IVideoDataTranscodings,
  StoredPlayerSettingsItem,
} from '~/interfaces/video.interface';
import { invert } from 'lodash';
import { CategoriesForExplanation, CategoriesForPose } from '~/config/category';

const env = process.env;

export function formatDigitToHHmmss(
  digit: number,
  zeroPad?: boolean,
  displayHour?: boolean,
  displayMin?: boolean,
): string {
  const startTime = digit;
  const NumberedStartTime = new Date(startTime * 1000);
  const utcStartTime = NumberedStartTime.toUTCString();
  const formattedStartTime =
    startTime >= 3600 || displayHour
      ? utcStartTime.replace(/.*(\d{2}:\d{2}:\d{2}).*/, '$1')
      : startTime >= 60 || displayMin
      ? utcStartTime.replace(/.*(\d{2}:\d{2}).*/, '$1')
      : utcStartTime.replace(/.*(\d{2}).*/, '$1');

  if (!zeroPad) {
    return formattedStartTime.startsWith('0')
      ? formattedStartTime.substr(1, formattedStartTime.length)
      : formattedStartTime;
  } else {
    return formattedStartTime;
  }
}

const changeFrame = (remainder: number, transcodingFrameRate: number) => {
  return Math.round((remainder + 0.000001) * transcodingFrameRate);
};

const getRemainder = (num: number) => {
  return parseFloat('0.' + String(num).split('.')[1]);
};

export function formatDigitToTimeFrame(
  digit: number,
  transcodingFrameRate: number,
  timeFormat: string,
) {
  const frame = changeFrame(getRemainder(digit), transcodingFrameRate);
  const frameStr = frame > transcodingFrameRate ? '00' : frame >= 10 ? frame : `0${frame}`;
  const time = formatDigitToHHmmss(
    frame > transcodingFrameRate ? Math.ceil(digit) : digit,
    true,
    timeFormat === 'h:mm:ss:ff',
    timeFormat === 'mm:ss:ff' || timeFormat === 'h:mm:ss:ff',
  );

  return time + ':' + frameStr;
}

export function formatTimeToDigit(time: string, transcodingFrameRate: number) {
  const timeArray = time.split(':');
  let second = 0;

  if (timeArray.length === 2) {
    second += parseInt(timeArray[0]);
    second += parseFloat(timeArray[1]) / transcodingFrameRate;
  } else if (timeArray.length === 3) {
    second += parseInt(timeArray[0]) * 60;
    second += parseInt(timeArray[1]);
    second += parseFloat(timeArray[2]) / transcodingFrameRate;
  } else {
    second += parseInt(timeArray[0]) * 3600;
    second += parseInt(timeArray[1]) * 60;
    second += parseInt(timeArray[2]);
    second += parseFloat(timeArray[3]) / transcodingFrameRate;
  }

  return second;
}

export function getTimeFormat(duration: number) {
  return duration >= 3600 ? 'h:mm:ss:ff' : duration >= 60 ? 'mm:ss:ff' : 'ss:ff';
}

export function makeVideoIdHash(length: number) {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(() => {
      resolve();
    }, ms);
  });
}

export function getImageDimension(file: File): Promise<{ width: number; height: number }> {
  return new Promise<{ width: number; height: number }>((resolve, reject) => {
    const img = new Image();

    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    img.onload = function () {
      resolve({
        width: img.width,
        height: img.height,
      });
      URL.revokeObjectURL(objectUrl);
    };

    img.onerror = function (e) {
      reject(e);
    };
  });
}

export function getBase64FromImage(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = function (e) {
      resolve(e.target?.result as string);
    };

    reader.onerror = function (e) {
      reject(e);
    };
  });
}

export function getStoragePlayerSettings(): StoredPlayerSettingsItem | null {
  const settings = localStorage.getItem('sparky-player-settings');

  try {
    const parsed = JSON.parse(settings || '{}');

    return parsed;
  } catch (e) {
    localStorage.removeItem('sparky-player-settings');

    return null;
  }
}

export function setStoragePlayerSettings(settings: StoredPlayerSettingsItem) {
  const currentSettings = (getStoragePlayerSettings() as { [key: string]: string }) ?? {};
  const nextSettings: { [key: string]: string } = {};

  if (currentSettings) {
    Object.keys(currentSettings).forEach((key) => {
      nextSettings[key] = currentSettings[key];
    });
  }

  Object.keys(settings).forEach((key) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    nextSettings[key] = settings[key];
  });

  localStorage.setItem('sparky-player-settings', JSON.stringify(nextSettings));
}

export function getViewerUrlByStage() {
  const stage = env.REACT_APP_STAGE;
  const protocol = 'https://';
  const domain = '.sparky.tv';

  switch (stage) {
    case 'development':
      return protocol + 'dev-www' + domain;
    case 'staging':
      return protocol + 'staging-www' + domain;
    case 'production':
      return protocol + 'www' + domain;
  }
}

export function getCategoryIdByName(name: string) {
  return invert({ ...CategoriesForPose, ...CategoriesForExplanation })[name];
}

export function getCategoryNameById(id: number) {
  return { ...CategoriesForPose, ...CategoriesForExplanation }[id];
}

export function getHighestQualityVideoItem(transcodings: IVideoDataTranscodings[]) {
  if (!transcodings.length) return null;

  const hq = transcodings
    .filter((v) => v.metadata.quality !== 'Original')
    .sort((a, b) => b.metadata.height - a.metadata.height);

  return hq[0];
}

export function getSelectedThumbnailItem(thumbnails: IVideoDataEditableThumbnails[] | undefined) {
  return thumbnails?.find((t) => t.isSelected);
}

type ThumbnailQuality = 'large' | 'medium' | 'small';

export function getSelectedThumbnailItemUrl(
  thumbnails: IVideoDataEditableThumbnails[] | undefined,
  quality: ThumbnailQuality = 'medium',
) {
  const selectedThumbnail = thumbnails?.find((t) => t.isSelected);

  if (!selectedThumbnail) return;

  return getThumbnailWithQuality(selectedThumbnail, quality) ?? '/assets/player/default-video.png';
}

export function getThumbnailWithQuality(
  thumbnail: IVideoDataEditableThumbnails,
  quality: ThumbnailQuality = 'medium',
) {
  const qualityIndex = quality === 'large' ? 0 : quality === 'small' ? 2 : 1;

  return thumbnail.thumbnailLocations.sort((a, b) => a.metadata.width - b.metadata.width)[
    qualityIndex
  ].url;
}
